import type { Appearance } from '../../api/types';

// A company's own colours (TF-109, TF-174): the whole colour scheme generated from its main colour,
// second colour and accent, in light, dark and every contrast level, by the same generator as
// DoCuLat's (design/color-scheme.ts). The generator is loaded only when a company has colours; what it
// wrote is kept in this browser per company, so public/theme.js puts it in place before the first paint
// of the next visit - no flash of DoCuLat's blue. Each person's light, dark or system choice still
// applies: the scheme has blocks for all of them, keyed on the same attributes as DoCuLat's.

/** The <style> the scheme lives in; public/theme.js writes the same one. */
export const STYLE_ID = 'company-colors';
const KEY = (tenantId: string) => `teamfolio.colors.${tenantId}`;

/**
 * What the generator writes changes now and then; a scheme kept by an older one is made again. 2: the
 * backgrounds are DoCuLat's whatever the colours (TF-174). public/theme.js checks the same number.
 */
export const VERSION = 2;

interface Cached {
  version: number;
  /** The colours the scheme was made from, as JSON. */
  colors: string;
  css: string;
}

function cached(tenantId: string): Cached | null {
  try {
    const value = JSON.parse(localStorage.getItem(KEY(tenantId)) ?? 'null') as Cached | null;
    return value && value.version === VERSION && typeof value.colors === 'string' && typeof value.css === 'string' ? value : null;
  } catch {
    return null;
  }
}

function keep(tenantId: string, value: Cached | null) {
  try {
    if (value) localStorage.setItem(KEY(tenantId), JSON.stringify(value));
    else localStorage.removeItem(KEY(tenantId));
  } catch {
    // not kept: generated again next time
  }
}

function show(css: string | null) {
  let style = document.getElementById(STYLE_ID);
  if (!css) {
    style?.remove();
    return;
  }
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }
  if (style.textContent !== css) style.textContent = css;
}

/** The scheme for a company's colours, as CSS hung on html:root so it wins over DoCuLat's own. */
export async function schemeCss(appearance: Appearance): Promise<string> {
  const { colorTokensCss } = await import('../../design/color-scheme');
  return colorTokensCss(appearance, 'html:root');
}

/**
 * Shows a company's colours: their scheme, or DoCuLat's when it has none or when the page belongs to
 * no company (tenantId null). A later call wins over an earlier one still generating.
 */
let latest = 0;
export async function applyCompanyColors(tenantId: string | null, appearance: Appearance | null): Promise<void> {
  const call = ++latest;
  if (!tenantId || !appearance) {
    if (tenantId) keep(tenantId, null);
    show(null);
    return;
  }
  const colors = JSON.stringify(appearance);
  const known = cached(tenantId);
  if (known?.colors === colors) {
    show(known.css);
    return;
  }
  const css = await schemeCss(appearance);
  if (call !== latest) return;
  keep(tenantId, { version: VERSION, colors, css });
  show(css);
}
